import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';

export interface SupportScreenshot {
    base64: string;
    mime: string;
}

@Injectable({ providedIn: 'root' })
export class SupportScreenshotService {
    constructor(@Inject(DOCUMENT) private document: Document) {}

    public get available(): boolean {
        return (
            typeof this.document.defaultView?.navigator.mediaDevices?.getDisplayMedia ===
            'function'
        );
    }

    /** Browser-native, user-initiated single-frame capture; never render the dashboard DOM. */
    public async capture(): Promise<SupportScreenshot> {
        if (!this.available)
            throw new Error(
                'Screen capture is unavailable. Attach a screenshot instead.'
            );
        const overlays = Array.from(
            this.document.querySelectorAll<HTMLElement>(
                '.pulse-support-modal, .pulse-support-backdrop'
            )
        ).map(element => ({
            element,
            value: element.style.getPropertyValue('visibility'),
            priority: element.style.getPropertyPriority('visibility'),
        }));
        let stream: MediaStream | undefined;
        let video: HTMLVideoElement | undefined;
        let timer: ReturnType<typeof setTimeout>;
        try {
            for (const { element } of overlays)
                element.style.setProperty('visibility', 'hidden', 'important');
            // Invoke directly during the button click: display capture requires user activation.
            const options = {
                video: { displaySurface: 'browser' },
                audio: false,
                preferCurrentTab: true,
                selfBrowserSurface: 'include',
            };
            stream =
                await this.document.defaultView.navigator.mediaDevices.getDisplayMedia(
                    options
                );
            video = this.document.createElement('video');
            video.muted = true;
            video.playsInline = true;
            video.srcObject = stream;
            const timeout = new Promise<never>((_resolve, reject) => {
                timer = setTimeout(
                    () =>
                        reject(
                            new Error(
                                'Screen capture timed out. Try again or attach a screenshot.'
                            )
                        ),
                    5000
                );
            });
            const image = (async (): Promise<SupportScreenshot> => {
                await video.play();
                // play() resolves once playback starts with a decoded frame. Do not wait
                // for a second frame: static shared tabs can stop producing new frames.
                if (!video.videoWidth || !video.videoHeight)
                    throw new Error(
                        'No screen image was received. Try again or attach a screenshot.'
                    );
                const scale = Math.min(
                    1,
                    Math.sqrt(2000000 / (video.videoWidth * video.videoHeight))
                );
                const canvas = this.document.createElement('canvas');
                canvas.width = Math.max(1, Math.round(video.videoWidth * scale));
                canvas.height = Math.max(1, Math.round(video.videoHeight * scale));
                const context = canvas.getContext('2d');
                if (!context)
                    throw new Error(
                        'Screen capture is unavailable. Attach a screenshot instead.'
                    );
                context.drawImage(video, 0, 0, canvas.width, canvas.height);
                const blob = await new Promise<Blob>((resolve, reject) => {
                    canvas.toBlob(
                        value =>
                            value
                                ? resolve(value)
                                : reject(new Error('Unable to save the screenshot.')),
                        'image/jpeg',
                        0.85
                    );
                });
                if (blob.size > 5 * 1024 * 1024)
                    throw new Error(
                        'The screenshot is too large. Choose the Pulse tab instead of the entire screen.'
                    );
                const base64 = await new Promise<string>((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = (): void =>
                        resolve(String(reader.result).split(',')[1]);
                    reader.onerror = (): void =>
                        reject(new Error('Unable to save the screenshot.'));
                    reader.readAsDataURL(blob);
                });
                return { base64, mime: 'image/jpeg' };
            })();
            return await Promise.race([image, timeout]);
        } finally {
            clearTimeout(timer);
            stream?.getTracks().forEach(track => track.stop());
            video?.pause();
            if (video) video.srcObject = null;
            for (const { element, value, priority } of overlays) {
                if (value) element.style.setProperty('visibility', value, priority);
                else element.style.removeProperty('visibility');
            }
        }
    }
}
